import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Popover, Typography, Input, List, Empty, Spin } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/free-solid-svg-icons';
import UserAvatar from '../../../elements/containers/UserAvatarContainer';
import useDebounce from '../../../elements/components/useDebounce';
import notification from '../../../elements/lib/NotificationWrapper';
import { Translation, getI18n } from 'react-i18next';
import './styles/TransferPopover.less';

const { Title, Text } = Typography;

const TransferPopover = ({
  load,
  limit,
  order,
  assign,
  list,
  total,
  isLoading,
  clearPages,
  partnershipId,
  smsConversationId,
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const scrollContainerRef = useRef(null);
  const debouncedSearchText = useDebounce(searchText, 500);

  const resetState = () => {
    setSelectedMember(null);
    setSearchText('');
    setCurrentPage(1);
  };

  const handleTransferClick = () => {
    if (selectedMember) {
      assign(
        partnershipId,
        smsConversationId,
        { assigned_to_user_profile_id: selectedMember?.profile?.id },
        (success) => {
          if (success) {
            notification.success(
              getI18n().t('sms_conversation_assign_success')
            );
          } else {
            notification.error(getI18n().t('sms_conversation_assign_failure'));
          }
        }
      );
      setOpen(false);
      resetState();
    }
  };

  const handleCancel = () => {
    setOpen(false);
    resetState();
  };

  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // Load initial data
  useEffect(() => {
    if (partnershipId && open) {
      // Clear existing pages when search changes
      clearPages(partnershipId);
      setCurrentPage(1);

      const filterObject = {
        role: '1,2',
        ...(debouncedSearchText.trim() !== '' && {
          search: debouncedSearchText,
        }),
      };

      load(partnershipId, 1, limit, order, filterObject);
    }
  }, [
    partnershipId,
    open,
    debouncedSearchText,
    clearPages,
    load,
    limit,
    order,
  ]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.8 && list.length < total) {
      setLoadingMore(true);
      const nextPage = currentPage + 1;

      const filterObject = {
        role: '1,2',
        ...(debouncedSearchText.trim() !== '' && {
          search: debouncedSearchText,
        }),
      };

      load(partnershipId, nextPage, limit, order, filterObject, () => {
        setLoadingMore(false);
        setCurrentPage(nextPage);
      });
    }
  }, [
    loadingMore,
    isLoading,
    list.length,
    total,
    currentPage,
    partnershipId,
    limit,
    order,
    debouncedSearchText,
    load,
  ]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const content = (
    <Translation>
      {(t) => (
        <div style={{ width: 320 }} onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
            }}
          >
            <Title level={5} style={{ color: '#4e77b8' }}>
              {t('sms_conversation_transfer')}
            </Title>
            <CloseOutlined
              style={{ fontSize: 14, cursor: 'pointer' }}
              onClick={handleClose}
            />
          </div>

          <div className='sms-members-search'>
            <Input
              placeholder={t('sms_conversation_transfer_search_placeholder')}
              prefix={<SearchOutlined style={{ color: '#4e77b8' }} />}
              value={searchText}
              onChange={handleSearchChange}
              style={{ marginBottom: 8 }}
            />
          </div>

          <div
            ref={scrollContainerRef}
            className='transfer-popover-scroll'
            style={{
              maxHeight: 280,
              overflowY: 'auto',
              padding: '0 8px',
            }}
          >
            {!isLoading && list.length === 0 ? (
              <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                <Empty
                  description={
                    <span style={{ color: '#8c8c8c' }}>
                      {searchText
                        ? t('sms_messages_no_members_found')
                        : t('sms_messages_no_members_available')}
                    </span>
                  }
                />
              </div>
            ) : (
              <>
                <div style={{ padding: '0 8px 8px 8px' }}>
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: 'bold',
                      color: '#4e77b8',
                    }}
                  >
                    {t('sms_messages_all')}
                  </Text>
                </div>
                <List
                  dataSource={list}
                  renderItem={(member) => (
                    <List.Item
                      style={{
                        padding: '8px 8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        backgroundColor:
                          selectedMember?.id === member.id
                            ? '#e6f4ff'
                            : 'transparent',
                      }}
                      onClick={() => setSelectedMember(member)}
                    >
                      <List.Item.Meta
                        avatar={
                          <UserAvatar profile={member?.profile} size={22} />
                        }
                        title={
                          <Text
                            style={{
                              fontSize: 14,
                              color:
                                selectedMember?.id === member.id
                                  ? '#1890ff'
                                  : '#262626',
                            }}
                          >
                            {member?.profile?.first_name}{' '}
                            {member?.profile?.last_name}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
                {loadingMore && (
                  <div style={{ textAlign: 'center', padding: '12px' }}>
                    <Spin size='small' />
                  </div>
                )}
              </>
            )}
          </div>

          <div
            style={{
              padding: '12px 16px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <Button onClick={handleCancel}>{t('action_cancel')}</Button>
            <Button
              type='primary'
              onClick={handleTransferClick}
              disabled={!selectedMember}
            >
              {t('sms_conversation_transfer')}
            </Button>
          </div>
        </div>
      )}
    </Translation>
  );

  return (
    <Translation>
      {(t) => (
        <Popover
          content={content}
          trigger='click'
          visible={open}
          onVisibleChange={(visible) => setOpen(visible)}
          placement='bottomRight'
          overlayClassName='transfer-popover'
          overlayInnerStyle={{
            borderRadius: '8px',
            padding: '0px',
            boxShadow:
              '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Button
            type='text'
            className='action-btn'
            onClick={() => setOpen((prev) => !prev)}
          >
            <FontAwesomeIcon icon={faShare} />
            {t('sms_conversation_transfer')}
          </Button>
        </Popover>
      )}
    </Translation>
  );
};

export default TransferPopover;
